// Cajón de «Teléfono». Es la ficha que el vendedor le enseña al cliente: el equipo, su precio, sus
// promos y con qué pagarlo. El botón de abajo arranca la venta con ese SKU.
import type { ReactNode } from 'react';
import { Gift, Plus, Smartphone, Tag } from 'lucide-react';
import type { PortalCatalogRow } from '@/api/services/retail-wizard/contract';
import { financierName } from '@/domains/credit/origination/lib/originationMetrics';
import { financiersFor, sureKeepFinances } from '@/domains/sales/wizard/cardBenefits';
import { CreditPromotionsGroup } from '@/domains/sales/wizard/components/CreditPromotionsGroup';
import { LevelChip } from '@/domains/sales/wizard/components/LevelChip';
import {
    PromotionGroup,
    PromotionGroupRow,
} from '@/domains/sales/wizard/components/PromotionGroup';
import type { ScopeWidePromotion } from '@/domains/sales/wizard/types';
import { cn } from '@/lib/cn';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { formatMXN } from '@/shared/lib/format/money';
import { productTierLabel } from '@/shared/lib/format/productTier';
import { useCompactViewport } from '@/shared/patterns/AppShell/useCompactViewport';
import { OutlinePill, StatusPill, TONE_OUTLINE_CLASS } from '@/shared/patterns/Pills';
import { formatPromotionValidity } from '@/shared/patterns/Promotions';
import { SideDrawer } from '@/shared/patterns/SideDrawer';
import {
    availableColors,
    categoryLabel,
    productLabel,
    productVariant,
} from '../../catalog/lib/catalogExplorer';
import { SALE_UNAVAILABLE_GAP, UNDETAILED_GAP } from '../../catalog/lib/catalogPromotions';
import { availableOf } from '../../pos/lib/posCatalog';
import { phonePromotions, promotionCount, type PhonePromotions } from '../lib/phonePromotions';
import { isSellable } from '../lib/phoneSale';
import { variantCapacityLabel } from '@/shared/lib/format/capacity';

export const START_SALE_LABEL = 'Iniciar venta con este teléfono';
export const SOLD_OUT_REASON = 'No hay unidades de este teléfono en tu tienda.';
export const REMOTE_PROTECTION_TEXT = 'Incluye protección remota';

const REASON_ID = 'phone-sale-drawer-reason';

function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section aria-label={title} className="mb-5">
            <h3 className="mb-2 text-[13px] font-semibold">{title}</h3>
            {children}
        </section>
    );
}

function stockText(available: number): string {
    if (available === 0) return 'Agotado';
    return `${String(available)} ${available === 1 ? 'pieza' : 'piezas'}`;
}

/** Las filas de la ficha técnica; un dato que el catálogo no trae no se pinta. */
function specRows(
    item: PortalCatalogRow,
    catalog: PortalCatalogRow[],
): { label: string; value: string }[] {
    const colors = availableColors(item, catalog);
    const rows = [
        { label: 'Marca', value: item.brand },
        { label: 'Modelo', value: item.model },
        { label: 'Capacidad', value: variantCapacityLabel(item) },
        { label: 'Color', value: item.color },
        { label: 'Gama', value: item.tier.trim() === '' ? '' : productTierLabel(item.tier) },
        { label: 'Colores disponibles', value: colors.length > 1 ? colors.join(', ') : '' },
        { label: 'Stock en tienda', value: stockText(availableOf(item)) },
        { label: 'Seguridad', value: item.mdmEnabled ? REMOTE_PROTECTION_TEXT : '' },
        { label: 'Categoría', value: categoryLabel(item) },
    ];
    return rows.filter((row) => row.value.trim() !== '');
}

function PhoneHero({ item, promoted }: { item: PortalCatalogRow; promoted: boolean }) {
    const variant = productVariant(item);
    const description = item.description?.trim() ?? '';
    return (
        <div className="-mx-4 -mt-3.5 mb-4 border-b border-border">
            {item.imageUrl === null ? (
                <div className="flex h-40 items-center justify-center bg-muted/40 text-muted-foreground">
                    <Smartphone className="size-16 opacity-40" aria-hidden />
                </div>
            ) : (
                <img
                    src={item.imageUrl}
                    alt={productLabel(item)}
                    className="h-56 w-full bg-muted/40 object-contain p-3"
                />
            )}
            <div className="px-4 py-3.5">
                <h2 className="text-[20px] font-bold leading-tight">{productLabel(item)}</h2>
                {description === '' || description === productLabel(item) ? null : (
                    <p className="mt-1 text-[13px]">{description}</p>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[13px] text-muted-foreground">
                    {variant === '' ? null : <span>{variant}</span>}
                    {item.tier.trim() === '' ? null : (
                        <StatusPill tone="neutral">{productTierLabel(item.tier)}</StatusPill>
                    )}
                </div>
                <div className="mt-3 flex flex-wrap items-end gap-x-3 gap-y-1.5">
                    <div>
                        <div className="text-[11px] text-muted-foreground">Precio de contado</div>
                        <div className="text-[30px] font-bold leading-none tabular-nums">
                            {formatMXN(item.cashPriceCents / 100)}
                        </div>
                    </div>
                    {promoted ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-promo px-2.5 py-1 text-[12px] font-bold text-white">
                            <Gift className="size-3.5" aria-hidden />
                            Con promoción
                        </span>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

/** Promos con las piezas del wizard: verde las de venta y morado las de crédito. */
function PromotionsSection({ promotions }: { promotions: PhonePromotions }) {
    const { sale, credit, saleUnavailable, undetailed } = promotions;
    if (sale.length === 0 && credit.length === 0 && !saleUnavailable && !undetailed) return null;
    return (
        <Section title="Promociones">
            <PromotionGroup
                label="Promos de venta"
                icon={Tag}
                tone="discount"
                limit={Number.POSITIVE_INFINITY}
                items={sale.map((promotion) => ({
                    key: promotion.promotionId,
                    node: (
                        <PromotionGroupRow
                            icon={Tag}
                            tone="discount"
                            name={promotion.name}
                            description={promotion.benefitSummary}
                            meta={formatPromotionValidity(
                                promotion.validFrom,
                                promotion.validUntil,
                            )}
                        />
                    ),
                }))}
            />
            <CreditPromotionsGroup rows={credit} />
            {saleUnavailable ? (
                <p className="mt-2 text-[11.5px] text-muted-foreground">{SALE_UNAVAILABLE_GAP}</p>
            ) : null}
            {undetailed && !saleUnavailable ? (
                <p className="mt-2 text-[11.5px] text-muted-foreground">{UNDETAILED_GAP}</p>
            ) : null}
        </Section>
    );
}

/** Si cada nivel alcanza al equipo a crédito, según la fila del BFF. */
function LevelsSection({ item }: { item: PortalCatalogRow }) {
    if (!sureKeepFinances(item) || item.levels === null || item.levels.length === 0) return null;
    return (
        <Section title="A crédito por nivel">
            <ul className="divide-y divide-border rounded-lg border border-border">
                {item.levels.map((fit) => (
                    <li
                        key={fit.level}
                        className="flex items-center justify-between gap-3 px-3 py-2 text-[13px]"
                    >
                        <LevelChip level={fit.level} size="sm" />
                        <span
                            className={cn(
                                'text-right font-medium',
                                !fit.eligible && 'text-muted-foreground',
                            )}
                        >
                            {!fit.eligible
                                ? 'No disponible'
                                : fit.minDownPaymentCents === undefined
                                  ? 'Disponible'
                                  : `Enganche desde ${formatMXN(fit.minDownPaymentCents / 100)}`}
                        </span>
                    </li>
                ))}
            </ul>
        </Section>
    );
}

/** Lo que trae un kit, con el nombre del catálogo cuando el SKU está cargado. */
function ComponentsSection({
    item,
    catalog,
}: {
    item: PortalCatalogRow;
    catalog: PortalCatalogRow[];
}) {
    const components = item.components ?? [];
    if (components.length === 0) return null;
    const nameOf = (sku: string): string => {
        const found = catalog.find((candidate) => candidate.sku === sku);
        return found === undefined ? sku : itemDisplayName(found);
    };
    return (
        <Section title="Incluye">
            <ul className="space-y-1 text-[13px]">
                {components.map((component) => (
                    <li key={component.sku}>
                        {component.qty} × {nameOf(component.sku)}
                    </li>
                ))}
            </ul>
        </Section>
    );
}

export function PhoneSaleDrawer({
    item,
    catalog,
    scopeWidePromotions,
    onClose,
    onSell,
}: {
    /** `null` = cerrado. */
    item: PortalCatalogRow | null;
    /** Teléfonos cargados: de ahí salen los colores del mismo modelo. */
    catalog: PortalCatalogRow[];
    /** Promos de crédito de toda la tienda; aplican a cualquier teléfono que SureKeep financie. */
    scopeWidePromotions?: readonly ScopeWidePromotion[];
    onClose: () => void;
    onSell: (sku: string) => void;
}) {
    const compact = useCompactViewport();
    if (item === null) return null;

    const sellable = isSellable(item);
    const promotions = phonePromotions(item, scopeWidePromotions);
    const financiers = financiersFor(item);
    const specs = specRows(item, catalog);

    return (
        <SideDrawer
            open
            onClose={onClose}
            title="Detalle del teléfono"
            placement={compact ? 'bottom' : 'right'}
            className={compact ? undefined : 'w-[460px]'}
            footer={
                <div className="w-full">
                    <button
                        type="button"
                        disabled={!sellable}
                        aria-describedby={sellable ? undefined : REASON_ID}
                        onClick={() => {
                            onSell(item.sku);
                        }}
                        className={cn(
                            'inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50',
                            compact ? 'h-12 text-[15px]' : 'h-11 text-[14px]',
                        )}
                    >
                        <Plus className="size-4" aria-hidden />
                        {START_SALE_LABEL}
                    </button>
                    {sellable ? null : (
                        <p
                            id={REASON_ID}
                            className="mt-1.5 text-center text-[11px] text-destructive"
                        >
                            {SOLD_OUT_REASON}
                        </p>
                    )}
                </div>
            }
        >
            <PhoneHero
                item={item}
                promoted={promotionCount(promotions) > 0 || promotions.undetailed}
            />

            <PromotionsSection promotions={promotions} />

            <ComponentsSection item={item} catalog={catalog} />

            <Section title="Ficha técnica">
                <dl className="divide-y divide-border rounded-lg border border-border">
                    {specs.map((row) => (
                        <div
                            key={row.label}
                            className="flex items-baseline justify-between gap-3 px-3 py-2 text-[13px]"
                        >
                            <dt className="text-muted-foreground">{row.label}</dt>
                            <dd className="text-right font-medium">{row.value}</dd>
                        </div>
                    ))}
                </dl>
                <p className="mt-1.5 text-right font-mono text-[10.5px] text-muted-foreground">
                    SKU {item.sku}
                </p>
            </Section>

            <LevelsSection item={item} />

            <div className="mb-2 flex flex-wrap items-center gap-1.5 text-[12.5px]">
                <span className="text-muted-foreground">
                    {financiers.length === 0 ? 'Solo de contado' : 'Puedes pagarlo con:'}
                </span>
                {financiers.map((code) => (
                    <OutlinePill
                        key={code}
                        outline={TONE_OUTLINE_CLASS.neutral}
                        className="px-2 py-0.5 text-[12px] font-medium"
                    >
                        {financierName(code)}
                    </OutlinePill>
                ))}
            </div>
        </SideDrawer>
    );
}
